/**
 * Landing page product offer — colourway swatches, optionally as a carousel.
 *
 * Each swatch carries its own product shot and, optionally, its own price, compare-at price and
 * link. Everything comes from data- attributes written by sections/vp-lp-product.liquid, so the
 * section stays editable in the theme editor and this file never needs to know about a specific
 * product.
 *
 * Two modes, decided by whether the section rendered a carousel track:
 *
 *   without a track — picking a swatch swaps the single image in place.
 *   with a track    — one slide per colourway is built from the swatches themselves, and picking
 *                     a swatch scrolls to its slide. Scrolling the track the other way marks the
 *                     matching swatch, so the two stay in step whichever the customer drives.
 *
 * Building the slides here rather than in Liquid keeps them in step with the swatches by
 * construction: they are the same list, read from the same attributes. The first slide is the
 * section's own image and stays server-rendered, so the largest image on the page is in the markup.
 *
 * A swatch that omits a field falls back to whatever the section itself is set to, captured once
 * on init. That matters when only some colourways carry a price: without the fallback, picking an
 * expensive one and then a standard one would leave the expensive price on screen.
 *
 * The swap has to rewrite `srcset`, not just `src`. The panel image carries a width ladder so it
 * stays sharp on retina, and whenever a `srcset` is present the browser picks from it and ignores
 * `src` entirely — rewriting `src` alone changes nothing on screen. The ladder here mirrors
 * snippets/vp-img-srcset.liquid; keep the two in step.
 */
(function () {
  // Swatch markup is re-rendered wholesale when the section is edited, so track initialised roots
  // rather than stamping an attribute the editor would restore from the server HTML.
  const initialized = new WeakSet();

  // Same widths as snippets/vp-img-srcset.liquid.
  const WIDTHS = [400, 600, 900, 1200, 1600, 2000];

  /**
   * Builds a CDN width ladder for `url`, or returns null for anything not served by Shopify —
   * a hand-entered URL elsewhere would not understand the query string.
   */
  function srcsetFor(url) {
    if (!url) return null;
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/shop/') === -1) return null;
    const sep = url.indexOf('?') === -1 ? '?' : '&';
    return WIDTHS.map((w) => url + sep + 'width=' + w + ' ' + w + 'w').join(', ');
  }

  /**
   * Whether two URLs point at the same uploaded file. Compares the filename alone: the same image
   * is served from both the shop domain and cdn.shopify.com, under different path prefixes, so
   * comparing whole URLs reports two identical pictures as different ones.
   */
  function sameImage(a, b) {
    if (!a || !b) return false;
    const name = (u) => u.split('?')[0].split('/').pop();
    return name(a) === name(b);
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Writes text without discarding the richtext <p> the setting rendered. */
  function setText(el, value) {
    if (!el || value == null || value === '') return;
    const target = el.querySelector('p') || el;
    target.textContent = value;
  }

  function readText(el) {
    if (!el) return null;
    return (el.querySelector('p') || el).textContent;
  }

  /**
   * Fills the track with a slide per colourway and wires the dots.
   * Returns a controller, or null when there is nothing to page through.
   */
  function buildCarousel(root, track, swatches, base) {
    const firstSlide = track.querySelector('[data-vp-lp-slide]');
    if (!firstSlide) return null;

    const slides = [firstSlide];
    const slideOf = new Map();

    // The section's image is usually also the first colourway. Where that holds, point the swatch
    // at the slide already in the markup instead of appending a duplicate of it.
    let claimedFirst = false;

    swatches.forEach((swatch) => {
      const url = swatch.dataset.image;
      if (!url) return;

      if (!claimedFirst && sameImage(url, base.image)) {
        slideOf.set(swatch, firstSlide);
        claimedFirst = true;
        return;
      }

      const slide = document.createElement('div');
      slide.className = 'vp-lp-product__slide';
      slide.setAttribute('data-vp-lp-slide', '');

      const picture = document.createElement('picture');
      picture.className = 'vp-lp-product__figure';

      const img = document.createElement('img');
      img.src = url;
      const ladder = srcsetFor(url);
      if (ladder) {
        img.srcset = ladder;
        img.sizes = base.sizes || '(max-width: 640px) 100vw, 50vw';
      }
      img.alt = swatch.dataset.label || base.alt || '';
      img.loading = 'lazy';
      if (base.imageWidth) img.setAttribute('width', base.imageWidth);
      if (base.imageHeight) img.setAttribute('height', base.imageHeight);

      picture.appendChild(img);
      slide.appendChild(picture);
      track.appendChild(slide);

      slides.push(slide);
      slideOf.set(swatch, slide);
    });

    if (slides.length < 2) return null;

    const prev = root.querySelector('[data-vp-lp-prev]');
    const next = root.querySelector('[data-vp-lp-next]');

    const dotsWrap = root.querySelector('[data-vp-lp-dots]');
    const dots = [];
    if (dotsWrap) {
      dotsWrap.hidden = false;
      slides.forEach((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'vp-lp-product__carousel-dot';
        dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
        dot.addEventListener('click', () => scrollTo(slide));
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    function scrollTo(slide) {
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    function currentIndex() {
      const mid = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const centre = slide.offsetLeft - track.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(centre - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    function markDots(index) {
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }

    /** The ends are hard stops, so the arrow that cannot go anywhere is disabled rather than hidden. */
    function markArrows(index) {
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= slides.length - 1;
    }

    if (prev) prev.addEventListener('click', () => scrollTo(slides[Math.max(0, currentIndex() - 1)]));
    if (next) {
      next.addEventListener('click', () =>
        scrollTo(slides[Math.min(slides.length - 1, currentIndex() + 1)])
      );
    }

    markDots(0);
    markArrows(0);

    return { slides, slideOf, scrollTo, currentIndex, markDots, markArrows, track };
  }

  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);

    const swatches = Array.from(root.querySelectorAll('[data-vp-lp-swatch]'));
    if (!swatches.length) return;

    const image = root.querySelector('[data-vp-lp-product-image]');
    const price = root.querySelector('[data-vp-lp-product-price]');
    const compare = root.querySelector('[data-vp-lp-product-compare]');
    const cta = root.querySelector('[data-vp-lp-product-cta]');

    // What the section is configured to show, used whenever a swatch leaves a field blank.
    const base = {
      image: image ? image.getAttribute('src') : null,
      imageWidth: image ? image.getAttribute('width') : null,
      imageHeight: image ? image.getAttribute('height') : null,
      alt: image ? image.getAttribute('alt') : null,
      srcset: image ? image.getAttribute('srcset') : null,
      sizes: image ? image.getAttribute('sizes') : null,
      price: readText(price),
      compare: readText(compare),
      link: cta ? cta.getAttribute('href') : null
    };

    const track = root.querySelector('[data-vp-lp-track]');
    const carousel = track ? buildCarousel(root, track, swatches, base) : null;

    function markSwatches(swatch) {
      swatches.forEach((s) => {
        const on = s === swatch;
        s.classList.toggle('vp-lp-product__swatch--selected', on);
        s.setAttribute('aria-pressed', String(on));
      });
    }

    function select(swatch, opts) {
      const fromScroll = opts && opts.fromScroll;
      markSwatches(swatch);

      const d = swatch.dataset;

      if (carousel) {
        // The slides are already the colourways, so paging to one is the whole job — rewriting the
        // first slide's image here would fight the carousel rather than help it.
        const slide = carousel.slideOf.get(swatch);
        if (slide && !fromScroll) {
          carousel.scrollTo(slide);
          const i = carousel.slides.indexOf(slide);
          if (i > -1) {
            carousel.markDots(i);
            carousel.markArrows(i);
          }
        }
      } else if (image) {
        const src = d.image || base.image;
        image.src = src;

        // Must follow `src`, and must be cleared rather than left stale when the new URL has no
        // ladder — otherwise the previous colourway's candidates keep winning.
        const ladder = d.image ? srcsetFor(src) : base.srcset;
        if (ladder) {
          image.setAttribute('srcset', ladder);
        } else {
          image.removeAttribute('srcset');
        }

        const w = d.imageWidth || base.imageWidth;
        const h = d.imageHeight || base.imageHeight;
        if (w) image.setAttribute('width', w);
        if (h) image.setAttribute('height', h);
        image.alt = d.label || base.alt || '';
        // A colourway the customer just picked should not fade in from a lazy load.
        image.loading = 'eager';
      }

      setText(price, d.price || base.price);
      setText(compare, d.compare || base.compare);
      if (cta) cta.href = d.link || base.link || cta.href;

      root.dispatchEvent(
        new CustomEvent('vp-lp-product:change', {
          bubbles: true,
          detail: { label: d.label || '', image: d.image || null, price: d.price || null }
        })
      );
    }

    swatches.forEach((s) => s.addEventListener('click', () => select(s)));

    if (carousel) {
      // Dragging the carousel selects the colourway it lands on, so the panel's price and link
      // follow the image however the customer got there.
      const swatchOfSlide = new Map();
      carousel.slideOf.forEach((slide, swatch) => {
        if (!swatchOfSlide.has(slide)) swatchOfSlide.set(slide, swatch);
      });

      let frame = null;
      carousel.track.addEventListener(
        'scroll',
        () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            const i = carousel.currentIndex();
            carousel.markDots(i);
            carousel.markArrows(i);
            const swatch = swatchOfSlide.get(carousel.slides[i]);
            if (swatch) select(swatch, { fromScroll: true });
          });
        },
        { passive: true }
      );
    } else {
      // Preload the other colourways so the swap is instant rather than a blank frame. With a
      // carousel the slides are real images already, so there is nothing to warm.
      swatches.forEach((s) => {
        const url = s.dataset.image;
        if (!url) return;
        const pre = new Image();
        const ladder = srcsetFor(url);
        // Warm the same candidate the swap will ask for, not the full-size master.
        if (ladder) {
          pre.srcset = ladder;
          pre.sizes = image ? image.sizes || '' : '';
        }
        pre.src = url;
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-vp-lp-product]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
